'use client';

import { c as _c } from "react/compiler-runtime";
import { type FactiiiTag, type SpaceTimeTag } from '@shared/all/domains/factiiis';
import { CheckCircle2, Clock, LocateFixed, MapPin, Plus, X } from 'lucide-react';
import { useState } from 'react';
import TimezoneSelect from 'react-timezone-select';
import Button from '../../common/Button';
import Input from '../../UI/Input';
type Entry = {
  location?: string;
  date?: string;
  time?: string;
  timezone?: SpaceTimeTag['timezone'];
};
type Props = {
  tag: FactiiiTag;
  needsLocation: boolean;
  needsDate: boolean;
  // DEVICE_LOCATION — offer a "use current location" button to fill the field.
  allowDevice: boolean;
  defaultTimezone: {
    value: string;
    label: string;
    offset: number;
  };
  addSpaceTime: (tag: FactiiiTag, entry: Entry) => boolean;
  removeSpaceTime: (tag: FactiiiTag, index: number) => void;
};
const formatStamp = (timestamp: Date | string | null | undefined) => {
  if (!timestamp) return '';
  const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

// One merged section for a factiii's space-time requirements. Completed entries
// render as read-only groups (location + when) each with an X; below them a
// single input area (location and/or date-time, per requirements) plus one Add.
export const SpaceTimeFactiii = t0 => {
  const $ = _c(57);
  const {
    tag,
    needsLocation,
    needsDate,
    allowDevice,
    defaultTimezone,
    addSpaceTime,
    removeSpaceTime
  } = t0;
  const spaceTimes = tag.postFactiiis?.[0]?.spaceTimes ?? [];
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [error, setError] = useState(false);
  const entries = spaceTimes.map(_temp).filter(_temp2);
  const label = needsLocation && needsDate ? "Location & time" : needsLocation ? "Location" : "Time";
  let t1;
  if ($[0] !== addSpaceTime || $[1] !== date || $[2] !== location || $[3] !== needsDate || $[4] !== needsLocation || $[5] !== setDate || $[6] !== setError || $[7] !== setLocation || $[8] !== setTime || $[9] !== tag || $[10] !== time || $[11] !== timezone) {
    t1 = () => {
      const ok = addSpaceTime(tag, {
        location: needsLocation ? location : undefined,
        date: needsDate ? date : undefined,
        time: needsDate ? time : undefined,
        timezone: needsDate ? timezone : undefined
      });
      if (ok) {
        setLocation("");
        setDate("");
        setTime("");
        setError(false);
      } else {
        setError(true);
      }
    };
    $[0] = addSpaceTime;
    $[1] = date;
    $[2] = location;
    $[3] = needsDate;
    $[4] = needsLocation;
    $[5] = setDate;
    $[6] = setError;
    $[7] = setLocation;
    $[8] = setTime;
    $[9] = tag;
    $[10] = time;
    $[11] = timezone;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const handleAdd = t1;
  let t2;
  if ($[13] !== setError || $[14] !== setLocation) {
    t2 = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(p => {
          setLocation(`${p.coords.latitude},${p.coords.longitude}`);
          setError(false);
        });
      }
    };
    $[13] = setError;
    $[14] = setLocation;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const handleHere = t2;
  const t3 = "flex flex-col space-y-2";
  let t4;
  if ($[16] !== label) {
    t4 = <span>{label}</span>;
    $[16] = label;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const t5 = entries.length > 0 && <CheckCircle2 className="size-4 text-green-500" />;
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <label className="flex items-center gap-2">{t4}{t5}</label>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== removeSpaceTime || $[22] !== tag) {
    t7 = t8 => {
      const {
        st: st_1,
        index: index_0
      } = t8;
      return <div key={index_0} className="flex items-center gap-2 rounded-md border border-ds-border-standard p-2"><div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-textSecondary">{st_1.latitude != null && st_1.longitude != null && <span className="flex items-center gap-1"><MapPin className="size-3.5" />{st_1.latitude}, {st_1.longitude}</span>}{st_1.timestamp && <span className="flex items-center gap-1"><Clock className="size-3.5" />{formatStamp(st_1.timestamp)}</span>}</div><button type="button" onClick={() => removeSpaceTime(tag, index_0)} aria-label="Remove entry" className="shrink-0 rounded-md p-1 text-destructive transition-colors hover:bg-destructive/10"><X className="size-4" /></button></div>;
    };
    $[21] = removeSpaceTime;
    $[22] = tag;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = entries.map(t7);
  let t9;
  if ($[24] !== allowDevice || $[25] !== handleHere || $[26] !== location || $[27] !== needsLocation || $[28] !== setError || $[29] !== setLocation) {
    t9 = needsLocation && <Input value={location} onChange={e => {
      setLocation(e);
      setError(false);
    }} label="Coordinates (34.052235, -118.243683)" className="grow" icons={allowDevice ? [<Button key="here" type="button" variant="TRANSPARENT" classNames="!p-1" onClick={handleHere} aria-label="Get current location" titleElement={<LocateFixed className="size-4" />} tooltip="Use current location" />] : []} />;
    $[24] = allowDevice;
    $[25] = handleHere;
    $[26] = location;
    $[27] = needsLocation;
    $[28] = setError;
    $[29] = setLocation;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== date || $[32] !== defaultTimezone || $[33] !== needsDate || $[34] !== setDate || $[35] !== setError || $[36] !== setTime || $[37] !== setTimezone || $[38] !== time || $[39] !== timezone) {
    t10 = needsDate && <><div className="flex items-center gap-2"><input type="date" title="Date Chooser" value={date} onChange={e_0 => {
          setDate(e_0.target.value);
          setError(false);
        }} className="w-full rounded-md border border-ds-border-standard bg-background-secondary p-2 text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary" /><input type="time" title="Time Chooser" value={time} onChange={e_1 => {
          setTime(e_1.target.value);
          setError(false);
        }} className="w-full rounded-md border border-ds-border-standard bg-background-secondary p-2 text-textPrimary focus:outline-none focus:ring-2 focus:ring-primary" /></div><div className="text-textPrimary"><TimezoneSelect value={timezone ?? defaultTimezone} onChange={tz => {
          const opt = tz as {
            value: string;
            label: string;
            offset?: number;
          };
          setTimezone({
            value: opt.value,
            label: opt.label,
            offset: opt.offset ?? defaultTimezone.offset
          });
        }} styles={{
          control: _temp3,
          menu: _temp4,
          singleValue: _temp5
        }} /></div></>;
    $[31] = date;
    $[32] = defaultTimezone;
    $[33] = needsDate;
    $[34] = setDate;
    $[35] = setError;
    $[36] = setTime;
    $[37] = setTimezone;
    $[38] = time;
    $[39] = timezone;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Plus className="size-4" />;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== handleAdd) {
    t12 = <Button type="button" classNames="w-full" borderTransparent={false} text="Add" icon={t11} onClick={handleAdd} />;
    $[42] = handleAdd;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== t10 || $[45] !== t12 || $[46] !== t9) {
    t13 = <div className="space-y-2 rounded-md border border-dashed border-ds-border-standard p-2">{t9}{t10}{t12}</div>;
    $[44] = t10;
    $[45] = t12;
    $[46] = t9;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== error || $[49] !== needsDate || $[50] !== needsLocation) {
    t14 = error && <p className="text-sm text-destructive">Enter valid{" "}{needsLocation && needsDate ? "coordinates and date/time" : needsLocation ? "coordinates" : "date and time"}.</p>;
    $[48] = error;
    $[49] = needsDate;
    $[50] = needsLocation;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== t13 || $[53] !== t14 || $[54] !== t6 || $[55] !== t8) {
    t15 = <div className={t3}>{t6}{t8}{t13}{t14}</div>;
    $[52] = t13;
    $[53] = t14;
    $[54] = t6;
    $[55] = t8;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  return t15;
};
export default SpaceTimeFactiii;
function _temp(st, index) {
  return {
    st,
    index
  };
}
function _temp2(t0) {
  const {
    st: st_0
  } = t0;
  return st_0.latitude != null && st_0.longitude != null || st_0.timestamp;
}
function _temp3(base) {
  return {
    ...base,
    backgroundColor: "rgba(var(--background-secondary-color))",
    borderColor: "rgba(var(--border-color))",
    color: "rgba(var(--text-color))"
  };
}
function _temp4(base_0) {
  return {
    ...base_0,
    backgroundColor: "rgba(var(--background-secondary-color))"
  };
}
function _temp5(base_1) {
  return {
    ...base_1,
    color: "rgba(var(--text-color))"
  };
}